import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/apiClient";
import { useLang } from "@/lib/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ListTree, FolderOpen, MessageSquare, CheckCircle2, Database, HardDrive, Archive, Ban } from "lucide-react";
import { fmtWIB, fmtBytes } from "@/lib/dateFmt";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [storage, setStorage] = useState(null);
  const { lang } = useLang();
  useEffect(() => {
    api.get("/admin/dashboard/stats").then(r => setStats(r.data));
    api.get("/admin/dashboard/storage").then(r => setStorage(r.data));
  }, []);
  const items = [
    { icon: ListTree, label: lang === "id" ? "Total Instrumen" : "Total Instruments", value: stats?.total_instruments ?? "-", tid: "stat-instruments" },
    { icon: CheckCircle2, label: lang === "id" ? "Terpublikasi" : "Published", value: stats?.published_instruments ?? "-", tid: "stat-published" },
    { icon: FolderOpen, label: lang === "id" ? "Kategori" : "Categories", value: stats?.total_categories ?? "-", tid: "stat-categories" },
    { icon: MessageSquare, label: lang === "id" ? "Respons Selesai" : "Completed Responses", value: stats?.completed_sessions ?? "-", tid: "stat-completed" },
  ];
  return (
    <AdminLayout>
      <div className="space-y-8">
        <div>
          <h1 className="font-display text-3xl tracking-tight" data-testid="admin-dashboard-title">{lang === "id" ? "Dasbor" : "Dashboard"}</h1>
          <p className="text-muted-foreground text-sm mt-1">{lang === "id" ? "Ringkasan aktivitas platform (waktu WIB)." : "Platform activity at a glance (WIB time)."}</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {items.map((it) => (
            <Card key={it.label} data-testid={it.tid} className="bg-card">
              <CardHeader className="pb-2"><div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground"><it.icon className="w-3.5 h-3.5" /> {it.label}</div></CardHeader>
              <CardContent className="font-display text-3xl">{it.value}</CardContent>
            </Card>
          ))}
        </div>

        {storage && (
          <Card>
            <CardHeader>
              <CardTitle className="font-display text-xl flex items-center gap-2"><Database className="w-5 h-5 text-terracotta-500" /> {lang === "id" ? "Ringkasan Penyimpanan" : "Storage Summary"}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <MiniStat icon={HardDrive} label={lang === "id" ? "Ukuran data" : "Data size"} value={fmtBytes(storage.data_size_bytes)} tid="stat-datasize" />
                <MiniStat icon={Database} label={lang === "id" ? "Rata-rata / respons" : "Avg / response"} value={fmtBytes(storage.estimated_avg_response_size_bytes)} tid="stat-avgsize" />
                <MiniStat icon={Archive} label={lang === "id" ? "Diarsipkan" : "Archived"} value={storage.total_archived} tid="stat-archived" />
                <MiniStat icon={Ban} label={lang === "id" ? "Dikecualikan" : "Excluded"} value={storage.total_excluded} tid="stat-excluded" />
                <MiniStat label={lang === "id" ? "Respons selesai" : "Completed"} value={storage.total_completed} tid="stat-storage-completed" />
                <MiniStat label={lang === "id" ? "Belum selesai" : "Incomplete"} value={storage.total_incomplete} tid="stat-incomplete" />
                <MiniStat label={lang === "id" ? "Terhapus (soft)" : "Soft-deleted"} value={storage.total_deleted_soft} tid="stat-softdel" />
                <MiniStat label={lang === "id" ? "30 hari terakhir" : "Last 30 days"} value={storage.responses_last_30_days} tid="stat-last30" />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-secondary/40 border border-border">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{lang === "id" ? "Respons Tertua & Terbaru" : "Oldest & Newest"}</div>
                  <div className="text-sm space-y-1">
                    <div><span className="text-muted-foreground">{lang === "id" ? "Tertua:" : "Oldest:"}</span> {fmtWIB(storage.oldest_completed_at, lang)}</div>
                    <div><span className="text-muted-foreground">{lang === "id" ? "Terbaru:" : "Newest:"}</span> {fmtWIB(storage.newest_completed_at, lang)}</div>
                  </div>
                </div>
                <div className="p-4 rounded-xl bg-secondary/40 border border-border">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{lang === "id" ? "Respons per Instrumen" : "Per instrument"}</div>
                  <div className="space-y-1 text-sm">
                    {storage.per_instrument.map(p => (
                      <div key={p.instrument_id} className="flex items-center justify-between" data-testid={`inst-count-${p.slug}`}>
                        <span className="truncate">{lang === "id" ? p.name_id : (p.slug)}</span>
                        <span className="font-mono">{p.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader><CardTitle className="font-display text-xl">{lang === "id" ? "Respons Terakhir" : "Recent Responses"}</CardTitle></CardHeader>
          <CardContent>
            {(stats?.recent_sessions || []).length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center">{lang === "id" ? "Belum ada respons." : "No responses yet."}</div>
            ) : (
              <div className="divide-y divide-border">
                {stats.recent_sessions.map((s) => (
                  <div key={s.id} className="py-3 flex items-center justify-between text-sm">
                    <div>
                      <div className="font-mono text-xs text-muted-foreground">{s.anonymous_code}</div>
                      <div>{s.instrument_slug}</div>
                    </div>
                    <div className="text-xs text-muted-foreground">{fmtWIB(s.completed_at, lang)}</div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}

const MiniStat = ({ icon: Icon, label, value, tid }) => (
  <div className="p-4 rounded-xl bg-card border border-border" data-testid={tid}>
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground">
      {Icon && <Icon className="w-3.5 h-3.5" />}
      {label}
    </div>
    <div className="font-display text-xl mt-1">{value}</div>
  </div>
);
